import type { FixedProductInput, CreditPurchaseConfig } from '@vy/types';
import { raise } from '@vy/utils';

// Normalized view-model that CreditsEditTable / CreditsSection consume for both
// the create flow (source: 'credits' → PlanInputForm.credits[]) and the
// edit/revise flow (source: 'fixedProducts' → PlanInputForm.fixedProducts[]).
//
// When source === 'credits'       → Edit and Remove buttons are rendered.
// When source === 'fixedProducts' → table is in readOnly mode; no Edit/Remove.
//
// The shape mirrors CreditPurchaseConfig so the row can be fed directly to
// extractCreditFormFromPurchaseItem when the user clicks Edit — no lossy cast.
export type CreditGrantView = {
  source: 'credits' | 'fixedProducts';
  // Index into the originating array (credits[] or fixedProducts[]).
  index: number;
  name: string;
  creditKind: CreditPurchaseConfig['creditKind'];
  creditTypeId: CreditPurchaseConfig['creditTypeId'];
  amount: number;
  schedule: CreditPurchaseConfig['schedule'];
  price?: CreditPurchaseConfig['price'];
  expiryDate?: CreditPurchaseConfig['expiryDate'];
  expirationPolicy?: CreditPurchaseConfig['expirationPolicy'];
};

// Adapts a CreditPurchaseConfig (create-flow form item) into a CreditGrantView.
export function fromCredit(item: CreditPurchaseConfig, index: number): CreditGrantView {
  return {
    source: 'credits',
    index,
    name: item.name,
    creditKind: item.creditKind,
    creditTypeId: item.creditTypeId,
    amount: item.amount,
    schedule: item.schedule,
    price: item.price ?? null,
    expiryDate: item.expiryDate ?? null,
    expirationPolicy: item.expirationPolicy ?? undefined,
  };
}

export function fromFixedProduct(product: FixedProductInput, originalIndex: number): CreditGrantView {
  const creditPurchaseConfig =
    product.creditPurchaseConfig ??
    raise(
      'fromFixedProduct called on a product without creditPurchaseConfig — caller must gate with isCreditPurchaseProduct',
    );
  return {
    source: 'fixedProducts',
    index: originalIndex,
    name: product.name ?? '',
    creditKind: creditPurchaseConfig.creditKind,
    creditTypeId: creditPurchaseConfig.creditTypeId,
    amount: creditPurchaseConfig.amount,
    schedule: creditPurchaseConfig.schedule,
    price: creditPurchaseConfig.price ?? null,
    expiryDate: creditPurchaseConfig.expiryDate ?? null,
    expirationPolicy: creditPurchaseConfig.expirationPolicy ?? undefined,
  };
}

// Adapts a CreditGrantView back to CreditPurchaseConfig for reverse-mapping
// into the AddCreditDrawer form (via extractCreditFormFromPurchaseItem).
export function toCreditPurchaseItem(row: CreditGrantView): CreditPurchaseConfig {
  return {
    name: row.name,
    creditKind: row.creditKind,
    creditTypeId: row.creditTypeId,
    amount: row.amount,
    schedule: row.schedule,
    price: row.price ?? null,
    expiryDate: row.expiryDate ?? null,
    expirationPolicy: row.expirationPolicy ?? undefined,
  };
}
